import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, ArrowLeft, ExternalLink } from "lucide-react";
import axios from "axios";
import { FadeIn } from "@/components/FadeIn";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/events/${slug}`).then((r) => { setEvent(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="font-body text-sm text-muted-foreground">Loading...</p></div>;
  if (!event) return <div className="min-h-screen flex items-center justify-center"><p className="font-body text-sm text-muted-foreground">Event not found.</p></div>;

  const renderParagraphs = (text) => {
    if (!text) return null;
    return text.split("\n").filter(Boolean).map((p, i) => (
      <p key={i} className="font-body text-base md:text-lg leading-relaxed text-muted-foreground">{p}</p>
    ));
  };

  return (
    <div data-testid="event-detail-page">
      {/* Banner */}
      {event.featured_image && (
        <div className="w-full max-h-[50vh] overflow-hidden">
          <img src={event.featured_image} alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}

      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Back */}
          <FadeIn>
            <Link to="/events" className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-events">
              <ArrowLeft size={14} strokeWidth={1.5} /> Back to Events
            </Link>
          </FadeIn>

          {/* Header */}
          <FadeIn>
            <div className="mb-12">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="font-body text-[10px] tracking-wider uppercase border-[#5C6B5D] text-[#5C6B5D]">{event.event_type}</Badge>
                {event.contribution_type && (
                  <Badge variant="outline" className="font-body text-[10px] tracking-wider uppercase border-[hsl(48,12%,88%)] text-muted-foreground">{event.contribution_type}</Badge>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight font-light text-foreground mb-6">{event.title}</h1>
              <div className="flex flex-wrap gap-6 font-body text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} strokeWidth={1.5} />
                  {new Date(event.event_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  {event.event_end_date && ` to ${new Date(event.event_end_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`}
                </span>
                {event.location && (
                  <span className="flex items-center gap-1.5">
                    <MapPin size={14} strokeWidth={1.5} /> {event.location}
                  </span>
                )}
              </div>
              {event.organized_by && <p className="font-body text-sm text-muted-foreground mt-2">Organized by {event.organized_by}</p>}
            </div>
          </FadeIn>

          {/* Full Description */}
          {event.full_description && (
            <FadeIn>
              <div className="space-y-5 mb-16">{renderParagraphs(event.full_description)}</div>
            </FadeIn>
          )}

          {/* Who Is This For */}
          {event.who_is_this_for && (
            <>
              <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-12" />
              <FadeIn>
                <h2 className="font-heading text-2xl tracking-tight mb-4 text-foreground">Who Is This For</h2>
                <div className="space-y-3">{renderParagraphs(event.who_is_this_for)}</div>
              </FadeIn>
            </>
          )}

          {/* What Will Be Explored */}
          {event.what_will_be_explored && (
            <>
              <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-12" />
              <FadeIn>
                <h2 className="font-heading text-2xl tracking-tight mb-4 text-foreground">What Will Be Explored</h2>
                <div className="space-y-3">{renderParagraphs(event.what_will_be_explored)}</div>
              </FadeIn>
            </>
          )}

          {/* Schedule */}
          {event.schedule && (
            <>
              <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-12" />
              <FadeIn>
                <h2 className="font-heading text-2xl tracking-tight mb-4 text-foreground">Schedule</h2>
                <div className="space-y-3">{renderParagraphs(event.schedule)}</div>
              </FadeIn>
            </>
          )}

          {/* Registration & Contact */}
          <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-12" />
          <FadeIn>
            <div className="flex flex-col md:flex-row gap-8">
              {event.registration_link && (
                <a
                  href={event.registration_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase px-8 py-3 bg-[#5C6B5D] text-white hover:bg-[#5C6B5D]/90 transition-all duration-300"
                  data-testid="event-register"
                >
                  Register <ExternalLink size={14} strokeWidth={1.5} />
                </a>
              )}
              {event.contact_info && (
                <div>
                  <h4 className="font-body text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">Contact</h4>
                  <p className="font-body text-sm text-foreground">{event.contact_info}</p>
                </div>
              )}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
